import type { SubscriptionDetails } from '~/models/Subscription'
import type { LicenseDetails } from '~/models/License'
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import arrayUtils from '~/utils/arrayUtils'
import useLicenseStore from '~/stores/license'
import { StaticSubscriptions } from '~/models/Subscription'
import useTime from '~/composables/useTime'

export function useSubscription() {
  const { uniqueBy, unique } = arrayUtils()
  const { isoStringInFuture } = useTime()

  const {
    loadLicenses,
    getLatestEndDateByEan,
  } = useLicenseStore()

  const {
    validLicenses
  } = storeToRefs(useLicenseStore())

  const isLoading = ref(false)

  const setSubscriptionsDetails = async () => {
    isLoading.value = true
    try {
      await loadLicenses()
      const subscriptionSet = [...Object.values(StaticSubscriptions).map(sub => sub.ean)]
      const licencesSet: LicenseDetails[] = [...validLicenses.value]
      const subscriptionLicenses = licencesSet
        .filter(license => license.ean && subscriptionSet.includes(license.ean))
        .filter(license => license.endDate && isoStringInFuture(license.endDate))
        .filter(uniqueBy('ean'))

      const activeSubscriptions = subscriptionLicenses.map((license) => {
        return StaticSubscriptions.find(subscription => subscription.ean === license.ean)
      })

      const subscriptionGrades = activeSubscriptions
        .map(subscription => subscription?.grades)
        .filter(unique)
        .sort()

      const shownSubscriptions: SubscriptionDetails[] = []
      subscriptionGrades.map((grades) => {
        const premiumSubscription = activeSubscriptions
          .find(subscription => subscription?.type === 'premium' && subscription?.grades === grades)
        const plusSubscription = activeSubscriptions
          .find(subscription => subscription?.type === 'plus' && subscription?.grades === grades)
        const basicSubscription = activeSubscriptions
          .find(subscription => subscription?.type === 'basic' && subscription?.grades === grades)
        const individualSubscription = activeSubscriptions
          .find(subscription => subscription?.type === 'individual' && subscription?.grades === grades)
        const trialSubscription = activeSubscriptions
          .find(subscription => subscription?.type === 'trial' && subscription?.grades === grades)

        const subscription = premiumSubscription || plusSubscription || basicSubscription || individualSubscription || trialSubscription
        if (subscription) {
          shownSubscriptions.push(subscription)
        }
      })

      shownSubscriptions.forEach(subscription => subscription.endDate = getLatestEndDateByEan(subscription.ean))

      return shownSubscriptions.sort((a, b) => (a.grades > b.grades ? 1 : -1))
    } finally {
      isLoading.value = false
    }
  }

  const isSubscriptionTrial = (subscription: SubscriptionDetails) => subscription?.type === 'trial'
  const isSubscriptionBasic = (subscription: SubscriptionDetails) => subscription?.type === 'basic'
  const isSubscriptionIndividual = (subscription: SubscriptionDetails) => subscription?.type === 'individual'
  const isSubscriptionPlus = (subscription: SubscriptionDetails) => subscription?.type === 'plus'
  const isSubscriptionPremium = (subscription: SubscriptionDetails) => subscription?.type === 'premium'
  const isSubscriptionPlusOrPremium = (subscription: SubscriptionDetails) => isSubscriptionPlus(subscription) || isSubscriptionPremium(subscription)

  return {
    setSubscriptionsDetails,
    isSubscriptionTrial,
    isSubscriptionBasic,
    isSubscriptionIndividual,
    isSubscriptionPlus,
    isSubscriptionPremium,
    isLoading,
    isSubscriptionPlusOrPremium,
  }
}

export default useSubscription
